import React from 'react';
import * as deepEqual from 'react-fast-compare';
import { Drawer, Well } from '@pushly/aqe/lib/components';
import { useService } from '../../hooks/use-service';
import { AppService, DomainService } from '../../services';
import { DomainDto } from '../../dtos/domain';
import { getClassNames } from '../../_utils/classnames';
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '../../constants';
import { Form } from 'antd';
import { NotificationAutoSuggestFields } from '../../enums/notification-auto-suggest-fields.enum';
import { BetterSelect } from '../better-select/better-select';
import { FormInstance } from 'antd/lib/form';

interface IProps {
    className?: string,
    visible: boolean,
    domain: DomainDto,
    onSubmit: (updates: Partial<DomainDto>) => any,
    onClose: () => void,
}

const getChanges = (domain: DomainDto, form: FormInstance, validate: boolean = false) => {
    const updates: any = {};
    const values = form?.getFieldsValue() ?? {};

    if (domain && form) {
        // auto suggest fields
        const valueFields = [...values.notificationAutoSuggestFields ?? []];
        valueFields.sort();
        const domainFields = !domain.notificationAutoSuggestFields ? null : [...domain.notificationAutoSuggestFields];
        domainFields?.sort();
        if (!deepEqual(domainFields, valueFields)) {
            updates.notificationAutoSuggestFields = valueFields;
        }
    }

    return updates;
};

const NotificationAutomationEditor = (props: IProps) => {
    const appSvc = useService(AppService);
    const domainSvc = useService(DomainService);

    const [form] = Form.useForm();
    const [submitEnabled, setSubmitEnabled] = React.useState(false);

    const {
        visible,
        domain,
        onSubmit,
        onClose,
    } = props;

    const defaultAllowedFields = Object.values(NotificationAutoSuggestFields);

    return (
        <Drawer
            getContainer={appSvc.getAppContainer}
            className={getClassNames('ddf-notif-automation-editor')}
            title="Update Notification Automation"
            placement="right"
            closable={true}
            disableSubmit={!submitEnabled}
            onSubmit={async () => {
                await form.validateFields();
                const updates = getChanges(domain, form);

                if (Object.keys(updates).length > 0) {
                    const res = await domainSvc.updateDomainById(domain.id, updates);

                    if (res.ok) {
                        onSubmit(res.data);
                    }
                }
            }}
            onClose={onClose}
            visible={visible}
        >
            <Well
                title="Field Suggestions"
                mode="ghost"
                hideFooter={true}
            >
                <Form
                    { ...DEFAULT_DRAWER_FORM_ITEM_LAYOUT }
                    className="hide-inline-errors"
                    form={form}
                    onValuesChange={() => {
                        const changes = getChanges(domain, form);
                        setSubmitEnabled(Object.keys(changes).length > 0);
                    }}
                >
                    <Form.Item
                        className={getClassNames('ddf-notif-editor-whitelist')}
                        name="notificationAutoSuggestFields"
                        label="Fields"
                        initialValue={domain.notificationAutoSuggestFields ?? defaultAllowedFields}
                    >
                        <BetterSelect
                            size="small"
                            mode="multiple"
                            className="suggested fields"
                            placeholder="No suggested fields"
                            disableSearch={true}
                            defaultValue={domain.notificationAutoSuggestFields ?? defaultAllowedFields}
                            options={[
                                { value: NotificationAutoSuggestFields.TITLE, label: 'Title' },
                                { value: NotificationAutoSuggestFields.BODY, label: 'Body' },
                                { value: NotificationAutoSuggestFields.IMAGE_URL, label: 'Image' },
                                { value: NotificationAutoSuggestFields.KEYWORDS, label: 'Keywords' },
                            ]}
                        />
                    </Form.Item>
                </Form>
            </Well>
        </Drawer>
    );
};

export default NotificationAutomationEditor;
